import React, { useState, useEffect } from "react";
import { Alchemy, Network } from "alchemy-sdk";
import "./NFTList.scss";
import { convertIpfsToUrl, fetchTokenMetadata } from "../../../utils";
import { Search } from "../../../assets";
import CollectionMetadataDisplay from "../CollectionMetadataDisplay/CollectionMetadataDisplay";
import PdfViewer from "../PdfViewer/PdfViewer";
import ArchModal from "../ArchModal/ArchModal";

const ALCHEMY_API_KEY = "cBHgm4PNLXlDi1jcTDugv";
const ITEMS_PER_PAGE = 20;

const NFTList = ({ contractAddress, aesKey }) => {
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contractMetadata, setContractMetadata] = useState(null);
  const [selectedNFT, setSelectedNFT] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [nftTransforms, setNftTransforms] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const openModal = (nft) => setSelectedNFT(nft);
  const closeModal = () => setSelectedNFT(null);

  const getTransformClass = (orientation) => {
    switch (orientation) {
      case 2:
        return "flip-x";
      case 3:
        return "rotate-180";
      case 4:
        return "rotate-180 flip-x";
      case 5:
        return "rotate-90 flip-y";
      case 6:
        return "rotate-90";
      case 7:
        return "rotate-neg-90 flip-y";
      case 8:
        return "rotate-neg-90";
      default:
        return "";
    }
  };

  useEffect(() => {
    if (!contractAddress || contractAddress.trim().length < 20) return;

    let isCancelled = false;
    setCurrentPage(1);

    const fetchAllNfts = async () => {
      setLoading(true);
      setNfts([]);
      setContractMetadata(null);
      setNftTransforms({});

      try {
        const alchemy = new Alchemy({
          apiKey: ALCHEMY_API_KEY,
          network: Network.BASE_MAINNET,
        });

        let allNfts = [];
        let pageKey = undefined;

        do {
          const response = await alchemy.nft.getNftsForContract(
            contractAddress,
            {
              pageSize: 100,
              pageKey: pageKey,
            },
          );

          if (isCancelled) return;

          allNfts = [...allNfts, ...response.nfts];
          pageKey = response.pageKey;
        } while (pageKey);

        const nftsWithMetadata = await Promise.all(
          allNfts.map(async (nft) => {
            console.log("NFT:", nft);
            let metadata = nft.rawMetadata;
            console.log("NFT Metadata:", metadata);
            const tokenUri =
              typeof nft.tokenUri === "string"
                ? nft.tokenUri
                : nft.tokenUri?.raw || nft.tokenUri?.gateway;

            if (
              (!metadata ||
                (!metadata.image &&
                  !metadata.file &&
                  !metadata.animation_url &&
                  !metadata.video &&
                  !metadata.preview)) &&
              tokenUri
            ) {
              metadata = await fetchTokenMetadata(
                tokenUri,
                nft.tokenId,
                aesKey,
              );
            }
            console.log("NFT Name:", metadata);
            console.log("Collection Name:", nft.collection?.name);

            const videoFileTypes = ["mp4", "webm", "mov", "avi", "ogv"];
            const videoExts = [".mp4", ".webm", ".mov", ".avi", ".ogv"];
            const isVideoUri = (uri) =>
              uri && videoExts.some((ext) => uri.toLowerCase().includes(ext));
            const isVideoFileType = (ft) =>
              ft && videoFileTypes.includes(ft.toLowerCase());
            const isPdfFileType = (ft) => ft && ft.toLowerCase() === "pdf";

            let imageUrl = null;
            let isPdf = false;
            let pdfUrl = null;
            let isVideo = false;
            let videoUrl = null;
            let previewUrl = null;

            const ft = metadata?.fileType;

            if (isVideoFileType(ft)) {
              isVideo = true;
              if (metadata?.file) videoUrl = convertIpfsToUrl(metadata.file);
              if (metadata?.animation_url)
                videoUrl = convertIpfsToUrl(metadata.animation_url);
              if (metadata?.preview)
                previewUrl = convertIpfsToUrl(metadata.preview);
            } else if (isPdfFileType(ft)) {
              isPdf = true;
              const fileUri = metadata.file || metadata.image;
              if (fileUri) {
                pdfUrl = convertIpfsToUrl(fileUri);
                imageUrl = pdfUrl;
              }
            } else {
              const animUri = metadata?.animation_url || metadata?.video;
              if (animUri) {
                const animUrl = convertIpfsToUrl(animUri);
                if (isVideoUri(animUri) || isVideoUri(animUrl)) {
                  isVideo = true;
                  videoUrl = animUrl;
                  if (metadata?.preview)
                    previewUrl = convertIpfsToUrl(metadata.preview);
                }
              }

              if (!isVideo && (metadata?.image || metadata?.file)) {
                const imageUri = metadata.image || metadata.file;
                const resolvedUrl = convertIpfsToUrl(imageUri);
                if (isVideoUri(imageUri) || isVideoUri(resolvedUrl)) {
                  isVideo = true;
                  videoUrl = resolvedUrl;
                  if (metadata?.preview)
                    previewUrl = convertIpfsToUrl(metadata.preview);
                }
              }

              if (!isVideo && (metadata?.image || metadata?.file)) {
                const imageUri = metadata.image || metadata.file;
                imageUrl = convertIpfsToUrl(imageUri);
                isPdf =
                  imageUri.toLowerCase().includes(".pdf") ||
                  imageUrl.toLowerCase().includes(".pdf") ||
                  metadata.mediaType === "pdf" ||
                  (metadata.properties &&
                    metadata.properties.find(
                      (p) => p.trait_type === "Collection" && p.value,
                    ));

                if (isPdf) pdfUrl = imageUrl;
              }
            }

            const yearProp = metadata?.properties?.find(
              (p) => p.trait_type === "Year",
            );
            const year = yearProp?.value || metadata?.year || "";

            return {
              ...nft,
              metadata,
              imageUrl,
              isPdf,
              pdfUrl,
              isVideo,
              videoUrl,
              previewUrl,
              year,
              title: metadata?.name || `Token #${nft.tokenId}`,
              description: metadata?.description || "",
            };
          }),
        );

        if (!isCancelled) {
          setNfts(nftsWithMetadata);
        }
      } catch (err) {
        console.error("[CONTRACT] Error:", err);
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    fetchAllNfts();
    return () => (isCancelled = true);
  }, [contractAddress, aesKey]);

  const filteredNFTs = React.useMemo(() => {
    const value = searchTerm.trim().toLowerCase();
    if (!value) return nfts;

    return nfts.filter((nft) => {
      const title = (nft.title || "").toLowerCase();
      return title.includes(value);
    });
  }, [nfts, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const totalPages = Math.ceil(filteredNFTs.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentNFTs = filteredNFTs.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE,
  );

  return (
    <div>
      <div className="search-wrapper">
        <img src={Search} alt="Search" className="search-icon" />
        <input
          type="text"
          placeholder="Search..."
          className="search-input2"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <span className="clear-btn" onClick={() => setSearchTerm("")}>
            ✖
          </span>
        )}
      </div>

      {contractMetadata && (
        <div className="PreCard">
          <CollectionMetadataDisplay metadata={contractMetadata} />
        </div>
      )}

      {loading && (
        <div className="arch_d_loading">
          <div className="arch_d_loading__spinner"></div>
          <p className="arch_d_loading__text">Loading NFTs...</p>
        </div>
      )}

      {!loading && currentNFTs.length === 0 && (
        <div className="no-data">
          <p>No data available in this collection.</p>
        </div>
      )}

      {currentNFTs.length > 0 && (
        <>
          <div className="Category-content2">
            {currentNFTs.map((nft) => {
              const rotationClass = nftTransforms[nft.tokenId] || "";

              return (
                <div
                  key={`${contractAddress}-${nft.tokenId}`}
                  className="arch_d_nft-card"
                >
                  <div className="arch_d_nft-card__image-wrapper">
                    <div className="arch_d_nft-card__badge">
                      <div className="arch_d_nft-card__content">
                        <span className="arch_d_nft-card__year">
                          {nft.collection?.name?.match(/\d{4}/)?.[0] || ""}
                        </span>
                        <span className="arch_d_nft-card__label">
                          Palestine
                        </span>
                      </div>
                    </div>

                    {nft.isPdf && nft.pdfUrl ? (
                      <PdfViewer
                        pdfUrl={nft.pdfUrl}
                        height="100%"
                        objectFit="cover"
                      />
                    ) : nft.isVideo ? (
                      <div className="PreCardd">
                        <div className="PreCard-canvas">
                          {nft.previewUrl ? (
                            <img
                              src={nft.previewUrl}
                              alt={nft.title}
                              className="arch-video-preview-img"
                              draggable="false"
                              onContextMenu={(e) => e.preventDefault()}
                              onError={(e) => (e.target.style.display = "none")}
                            />
                          ) : (
                            <video
                              src={nft.videoUrl}
                              className="arch-video-thumbnail"
                              muted
                              playsInline
                              preload="metadata"
                              onContextMenu={(e) => e.preventDefault()}
                            />
                          )}
                        </div>
                      </div>
                    ) : nft.imageUrl ? (
                      <img
                        src={nft.imageUrl}
                        alt={nft.title}
                        className={`arch_d_nft-card__image ${rotationClass}`}
                        draggable="false"
                        onContextMenu={(e) => e.preventDefault()}
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    ) : (
                      <div className="arch_d_nft-card__placeholder">
                        <span>No media</span>
                      </div>
                    )}
                  </div>

                  <div className="arch_d_nft-card__header">
                    <div className="arch_d_nft-card__field">
                      <h4 className="arch_d_nft-card__title">{nft.title}</h4>
                      <span>
                        {nft.collection?.name?.match(/\d{4}/)?.[0] ||
                          nft.year ||
                          ""}
                      </span>
                    </div>

                    <button className="buy-btn" onClick={() => openModal(nft)}>
                      Read more
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    className={`pagination-number ${
                      page === currentPage ? "active" : ""
                    }`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ),
              )}

              <button
                className="pagination-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}

          {selectedNFT && <ArchModal item={selectedNFT} onClose={closeModal} />}
        </>
      )}
    </div>
  );
};

export default NFTList;
